import { useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';

import authApi from '../../api/authApi';
import { applyServerErrors, getErrorMessage } from '../../utils/errorHandler';

const schema = yup.object({
  password: yup.string().min(8, 'Mínimo 8 caracteres').required('La contraseña es obligatoria'),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref('password')], 'Las contraseñas no coinciden')
    .required('Confirma tu contraseña'),
});

// Segundo paso de "olvide mi contrasena": llega desde el enlace del correo (token + email en la
// URL, ver ResetPasswordNotification en la API) y define la contrasena nueva.
export default function ResetPasswordForm({ token, email }) {
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState('');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(schema), defaultValues: { password: '', password_confirmation: '' } });

  const onSubmit = async (data) => {
    setFormError('');
    try {
      await authApi.resetPassword({ token, email, ...data });
      setDone(true);
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      // El error de token invalido/expirado llega en "email", que no es un campo visible aqui.
      if (!handled || error?.response?.data?.errors?.email) {
        setFormError(
          error?.response?.data?.errors?.email?.[0] || getErrorMessage(error, 'No se pudo restablecer la contraseña.')
        );
      }
    }
  };

  if (!token || !email) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">El enlace no es válido. Solicita uno nuevo para restablecer tu contraseña.</Alert>
        <Button variant="contained" component={RouterLink} to="/recuperar-password">
          Solicitar un nuevo enlace
        </Button>
      </Stack>
    );
  }

  if (done) {
    return (
      <Stack spacing={2}>
        <Alert severity="success">Tu contraseña se restableció correctamente. Ya puedes iniciar sesión.</Alert>
        <Button variant="contained" size="large" component={RouterLink} to="/login">
          Iniciar sesión
        </Button>
      </Stack>
    );
  }

  return (
    <Stack component="form" spacing={3} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography variant="body2" color="text.secondary">
        Ingresa una contraseña nueva para <strong>{email}</strong>.
      </Typography>

      {formError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/recuperar-password">
              Nuevo enlace
            </Button>
          }
        >
          {formError}
        </Alert>
      )}

      <TextField
        label="Nueva contraseña"
        type="password"
        autoComplete="new-password"
        error={Boolean(errors.password)}
        helperText={errors.password?.message}
        {...register('password')}
      />
      <TextField
        label="Confirmar contraseña"
        type="password"
        autoComplete="new-password"
        error={Boolean(errors.password_confirmation)}
        helperText={errors.password_confirmation?.message}
        {...register('password_confirmation')}
      />

      <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
        {isSubmitting ? 'Guardando...' : 'Restablecer contraseña'}
      </Button>
    </Stack>
  );
}

ResetPasswordForm.propTypes = {
  token: PropTypes.string,
  email: PropTypes.string,
};
